import { useEffect, useState } from 'react'
import { FieldRenderer } from '../../components/FieldRenderer'
import type { FieldDefinition, FormSchema, FormSection } from '@/features/contract/types'
import { useDebouncedElementSectionUpdate } from '../hooks'

type Props = {
  contractId: number
  elementId: number
  extras: Record<string, unknown>
  schema: FormSchema
  section: FormSection
}

/** Renders one product section's fields for a single element, auto-saving into element.extras. */
export function ElementSectionForm({ contractId, elementId, extras, schema, section }: Props) {
  const updater = useDebouncedElementSectionUpdate(contractId, elementId, section.code)
  const [localValues, setLocalValues] = useState<Record<string, unknown>>(extras)

  useEffect(() => setLocalValues(extras), [extras])

  const placed = (schema.placements ?? [])
    .filter(p => p.sectionId === section.id)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const fieldById = new Map(schema.fields.map(f => [f.id, f]))
  const fields: Array<{ field: FieldDefinition; colWidth: number }> = placed
    .map(p => {
      const field = fieldById.get(p.fieldId)
      return field ? { field, colWidth: p.colWidth } : null
    })
    .filter((x): x is { field: FieldDefinition; colWidth: number } => x !== null)

  const handleChange = (fieldCode: string) => (value: unknown) => {
    if (valuesEqual(localValues[fieldCode], value)) return
    setLocalValues(prev => ({ ...prev, [fieldCode]: value }))
    updater.queue({ [fieldCode]: value })
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-4">
      <header className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-slate-700">{section.labelPl}</h4>
        <SaveBadge status={updater.status} />
      </header>
      {fields.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Brak pól w tej sekcji.</p>
      ) : (
        <div className="grid grid-cols-12 gap-x-4 gap-y-4">
          {fields.map(({ field: f, colWidth }) => (
            <div key={f.fieldCode} style={{ gridColumn: `span ${clampWidth(colWidth)}` }}>
              <FieldRenderer
                field={f}
                value={localValues[f.fieldCode]}
                mode="EDIT"
                onChange={handleChange(f.fieldCode)}
                contractId={contractId}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (typeof a === 'object' || typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  return false
}

function clampWidth(w: number | undefined): number {
  if (!w || w < 1) return 12
  if (w > 12) return 12
  return w
}

function SaveBadge({ status }: { status: 'idle' | 'pending' | 'success' | 'error' }) {
  if (status === 'idle') return null
  const base = 'inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full'
  if (status === 'pending') return <span className={`${base} bg-slate-100 text-slate-600`}>Zapisywanie</span>
  if (status === 'success') return <span className={`${base} bg-emerald-50 text-emerald-700`}>Zapisano</span>
  return <span className={`${base} bg-red-50 text-red-700`}>Błąd zapisu</span>
}
